import { z } from "zod";
//#region src/stream-spec.ts
/**
* Subject validation for APP-AUTHORED specs — the security control behind the
* operator boundary `safeParse` (AD-4.7). Each subject is a dot-delimited list
* of NATS tokens: alphanumerics / `-` / `_`, the single-token wildcard `*`, or
* a trailing `>` (last token only). SYSTEM subjects (anything starting with `$`,
* e.g. `$JS.>` / `$SYS.>`) are REJECTED — only the privileged operator identity
* touches `$JS.API.>`, never an app-authored {@link StreamSpec}.
*
* @param s - candidate NATS subject string
* @returns `true` when `s` is a well-formed, non-system NATS subject
*/
const SUBJECT_RE = /^(?:[A-Za-z0-9_-]+|\*)(?:\.(?:[A-Za-z0-9_-]+|\*))*(?:\.>)?$|^>$/;
function isValidNatsSubject(s) {
	if (s.length === 0 || s.length > 255 || s.startsWith("$")) return false;
	return SUBJECT_RE.test(s);
}
/**
* Declarative description of a single JetStream stream the platform should
* reconcile. App-authored data — never code — so the operator can `safeParse`
* it at the trust boundary before it drives a privileged broker operation.
*
* The `retention` / `maxAgeSeconds` knobs map to NATS `StreamConfig.retention`
* / `max_age` in the node provisioner. `metadata` is persisted into
* `StreamConfig.metadata`; the provisioner stamps the reserved keys
* (`zion.witness.ai/managed-by` | `.../created-by` | `.../owner-ref` |
* `.../owner-delete-policy`) so it can read ownership back for orphan detection
* and owner-cascade. `onOwnerDelete` defaults to `'orphan'` — cascade-delete is
* strictly opt-in (streams are data-bearing).
*/
const StreamSpecSchema = z.object({
	name: z.string().min(1).max(255).regex(/^[A-Za-z0-9_-]+$/, "stream name: alnum/-/_ only"),
	subjects: z.array(z.string().refine(isValidNatsSubject, "invalid or system NATS subject")).min(1),
	retention: z.enum([
		"limits",
		"workqueue",
		"interest"
	]).optional(),
	maxAgeSeconds: z.number().int().positive().optional(),
	/**
	* De-duplication window in SECONDS → NATS `StreamConfig.duplicate_window`
	* (mapped to nanoseconds by the node provisioner). CREATE-only — consistent
	* with `retention` / `replicas` (an existing stream's dedup window is never
	* mutated on a re-provision). The agent-stream broker threads the per-kind
	* clamped `duplicateWindowSeconds` here so the §2 "dup 2m" policy is applied
	* explicitly rather than relying on the NATS server default.
	*/
	duplicateWindowSeconds: z.number().int().positive().optional(),
	/**
	* Maximum stream size in bytes → NATS `StreamConfig.max_bytes`. Mapped by
	* the node provisioner into CREATE + UPDATE configs + the drift comparison
	* (AD-12 — the platform DLQ spec threads its per-env value here).
	*/
	maxBytes: z.number().int().positive().optional(),
	/**
	* Replication factor → NATS `StreamConfig.num_replicas`. CREATE-only in the
	* node provisioner — the update merge retains the live value, so an
	* env-misconfigured reconcile can never clobber a live stream's replication
	* (AD-12).
	*/
	replicas: z.number().int().positive().optional(),
	/**
	* Persisted into NATS `StreamConfig.metadata`. Reserved keys (set by the
	* provisioner, not the caller): `'zion.witness.ai/managed-by'` |
	* `'.../created-by'` | `'.../owner-ref'` | `'.../owner-delete-policy'`.
	*/
	metadata: z.record(z.string(), z.string()).optional(),
	/** Opaque owner identity, e.g. `'fixture/<id>'`. The SDK never interprets it. */
	ownerRef: z.string().min(1).optional(),
	/** Default `'orphan'` (applied at read). Cascade-delete is strictly opt-in. */
	onOwnerDelete: z.enum(["delete", "orphan"]).optional()
}).strict();
/**
* Declarative description of a single JetStream **KV bucket** the platform
* should reconcile (dynamic-agent-streams G1). A KV bucket is a JetStream stream
* under the hood (`KV_<bucket>`), so this mirrors {@link StreamSpec} but carries
* only the KV knobs the operator declares for the static coordination set
* (`AGENT_LEASES`, `event-server-leader`, `s3-archiver-leader`). The node
* provisioner's {@link NatsStreamProvisioner.ensureKeyValue} creates-or-binds via
* the `nats.js` materialized-views `kv()` API; `replicas` + `history` are
* CREATE-ONLY (the underlying stream's limits are immutable after creation —
* binding an existing bucket never mutates them), mirroring the CREATE-only
* `num_replicas` handling on {@link StreamSpec}.
*
* Bucket names are the same NATS-token grammar as a stream name (alnum / `-` /
* `_`); the `nats.js` views layer prefixes the backing stream with `KV_`.
*/
const KvSpecSchema = z.object({
	/** The KV bucket name (NATS-legal, alnum/-/_; the backing stream is `KV_<bucket>`). */
	bucket: z.string().min(1).max(255).regex(/^[A-Za-z0-9_-]+$/, "kv bucket name: alnum/-/_ only"),
	/**
	* Replication factor for the backing stream (NATS allows 1 / 3 / 5).
	* CREATE-only — binding an existing bucket never re-applies it.
	*/
	replicas: z.number().int().positive().optional(),
	/**
	* Number of historical revisions kept per key (`KvLimits.history`). The
	* static coordination buckets use `history: 1` (latest-only leader locks).
	* CREATE-only — binding an existing bucket never re-applies it.
	*/
	history: z.number().int().positive().optional(),
	/**
	* Backing-stream `max_age` (the KV entry TTL) in SECONDS. The leader-election
	* coordination buckets set this so a dead leader's lock auto-expires after the
	* TTL (without it the lock never expires → no failover). UNLIKE replicas/history
	* (create-only), `max_age` IS reconciled on bind: `ensureKeyValue` updates an
	* existing bucket's backing stream in place when this differs (`max_age` is
	* updatable on a live JetStream stream; D22 fix-forward, never recreate).
	* Omitted → no TTL / infinite retention (e.g. the lease bucket).
	*/
	maxAgeSeconds: z.number().int().positive().optional()
}).strict();
//#endregion
export { KvSpecSchema, StreamSpecSchema, isValidNatsSubject };

//# sourceMappingURL=stream-spec.js.map